import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, Menu, Sun, Moon, Activity, Scale, X, Pill, ClipboardList, ClipboardPlus, Delete } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useTheme } from "@/contexts/ThemeContext";
import { useWeight } from "@/contexts/WeightContext";
import { useService } from "@/contexts/ServiceContext";
import { Link } from "react-router-dom";
import InterventionTrackerSheet from "@/components/InterventionTrackerSheet";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Header = ({ onMenuClick }) => {
  const { theme, setTheme } = useTheme();
  const { weight, setWeight, clearWeight, hasWeight } = useWeight();
  const { activeService } = useService();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [weightUnit, setWeightUnit] = useState("kg");
  const navigate = useNavigate();

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/search/all/${encodeURIComponent(query)}`, { params: { service: activeService } });
      setSearchResults(response.data);
      setShowResults(true);
    } catch (error) {
      console.error("Search error:", error);
    }
  };

  const handleSelectResult = (result) => {
    setShowResults(false);
    setSearchQuery("");
    if (result.type === "drug") {
      navigate(`/medications#${result.id}`);
    } else if (result.type === "procedure") {
      navigate(`/procedure/${result.id}`);
    } else {
      navigate(`/protocol/${result.id}`);
    }
  };

  const resultIcon = (type) => {
    if (type === "drug") return <Pill className="w-4 h-4 text-amber-400 shrink-0" />;
    if (type === "procedure") return <ClipboardList className="w-4 h-4 text-green-400 shrink-0" />;
    return <Activity className="w-4 h-4 text-primary shrink-0" />;
  };

  const resultLabel = (type) => {
    if (type === "drug") return "Drug";
    if (type === "procedure") return "Procedure";
    return "Protocol";
  };

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-14 items-center gap-2 sm:gap-4 px-2 sm:px-4">
        <Button 
          variant="ghost" 
          size="icon" 
          className="lg:hidden shrink-0" 
          onClick={onMenuClick}
          data-testid="mobile-menu-btn"
        >
          <Menu className="h-5 w-5" />
        </Button>
        
        <div className="flex-1 relative min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search..."
            className="pl-10 bg-secondary/30 border-transparent focus:border-primary"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            onFocus={() => searchQuery.length >= 2 && setShowResults(true)}
            onBlur={() => setTimeout(() => setShowResults(false), 200)}
            data-testid="search-input"
          />
          
          {showResults && searchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg z-50 max-h-[60vh] overflow-auto min-w-[280px]">
              {searchResults.slice(0, 12).map((result) => (
                <button
                  key={`${result.type}-${result.id}`}
                  className="w-full px-4 py-3 text-left hover:bg-accent flex items-center gap-2 first:rounded-t-lg last:rounded-b-lg"
                  onMouseDown={() => handleSelectResult(result)}
                  data-testid={`search-result-${result.id}`}
                >
                  {resultIcon(result.type)}
                  <span className="truncate">{result.name}</span>
                  <Badge variant="outline" className="ml-auto text-xs shrink-0">{resultLabel(result.type)}</Badge>
                </button>
              ))}
            </div>
          )}
        </div>
        
        {/* Intervention Log slide-out */}
        <InterventionTrackerSheet
          trigger={
            <Button
              variant="ghost"
              size="icon"
              title="Intervention Log"
              className="text-emerald-500 hover:bg-emerald-500/10 h-9 w-9 shrink-0"
              data-testid="header-intervention-log-btn"
            >
              <ClipboardPlus className="h-5 w-5" />
            </Button>
          }
        />

        {/* Weight Input */}
        <WeightDialog />
        
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          data-testid="theme-toggle"
        >
          {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </Button>
      </div>
    </header>
  );
};

function WeightDialog() {
  const { weight, setWeight, clearWeight, hasWeight } = useWeight();
  const [open, setOpen] = useState(false);
  const [unit, setUnit] = useState("kg");
  const [inputVal, setInputVal] = useState("");

  // Sync input when dialog opens
  useEffect(() => {
    if (open) {
      if (hasWeight) {
        setInputVal(unit === "lbs" ? Math.round(parseFloat(weight) * 2.205).toString() : weight);
      } else {
        setInputVal("");
      }
    }
  }, [open, unit, weight, hasWeight]);

  const handleNumpad = (num) => {
    if (inputVal.length >= 4) return;
    setInputVal(prev => prev + num);
  };

  const handleBackspace = () => {
    setInputVal(prev => prev.slice(0, -1));
  };

  const handleConfirm = () => {
    if (!inputVal) return;
    const val = parseFloat(inputVal);
    if (unit === "lbs") {
      setWeight(Math.round(val / 2.205).toString());
    } else {
      setWeight(inputVal);
    }
    setOpen(false);
  };

  const handleClear = () => {
    clearWeight();
    setInputVal("");
    setUnit("kg");
    setOpen(false);
  };

  const toggleUnit = () => {
    const newUnit = unit === "kg" ? "lbs" : "kg";
    if (inputVal) {
      const val = parseFloat(inputVal);
      if (newUnit === "lbs") {
        setInputVal(Math.round(val * 2.205).toString());
      } else {
        setInputVal(Math.round(val / 2.205).toString());
      }
    }
    setUnit(newUnit);
  };

  const displayKg = inputVal
    ? unit === "lbs" ? `${Math.round(parseFloat(inputVal) / 2.205)} kg` : `${inputVal} kg`
    : "—";

  return (
    <>
      <Button
        variant="ghost"
        onClick={() => setOpen(true)}
        className={`h-9 px-2 gap-1.5 font-mono text-sm shrink-0 ${hasWeight ? 'bg-green-500/10 border border-green-500/30 text-green-400' : 'text-muted-foreground'}`}
        data-testid="weight-dialog-trigger"
      >
        <Scale className="w-4 h-4" />
        {hasWeight ? `${weight} kg` : "Wt"}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-sm p-0 gap-0" data-testid="weight-dialog">
          <DialogHeader className="px-5 pt-5 pb-3">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Scale className="w-5 h-5" />
              Patient Weight
            </DialogTitle>
          </DialogHeader>

          <div className="px-5 pb-2">
            {/* Unit Toggle */}
            <div className="flex gap-1 mb-3 bg-secondary/30 rounded-lg p-1" data-testid="weight-unit-toggle">
              <button
                onClick={() => { if (unit !== "kg") toggleUnit(); }}
                className={`flex-1 py-2 rounded-md text-sm font-bold transition-all ${unit === "kg" ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}
                data-testid="weight-unit-kg"
              >
                kg
              </button>
              <button
                onClick={() => { if (unit !== "lbs") toggleUnit(); }}
                className={`flex-1 py-2 rounded-md text-sm font-bold transition-all ${unit === "lbs" ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}
                data-testid="weight-unit-lbs"
              >
                lbs
              </button>
            </div>

            {/* Display */}
            <div className="bg-secondary/20 border border-border rounded-xl p-4 text-center mb-3" data-testid="weight-display">
              <div className="text-5xl font-bold font-mono">
                {inputVal || <span className="text-muted-foreground">0</span>}
                <span className="text-xl text-muted-foreground ml-1">{unit}</span>
              </div>
              {unit === "lbs" && inputVal && (
                <div className="text-sm text-green-400 font-mono mt-1">= {displayKg}</div>
              )}
            </div>

            {/* Numpad */}
            <div className="grid grid-cols-3 gap-1.5 mb-3">
              {[1,2,3,4,5,6,7,8,9].map(n => (
                <button
                  key={n}
                  onClick={() => handleNumpad(n.toString())}
                  className="h-14 rounded-lg bg-secondary/40 hover:bg-secondary/80 text-xl font-bold font-mono transition-all active:scale-95"
                  data-testid={`numpad-${n}`}
                >
                  {n}
                </button>
              ))}
              <button
                onClick={handleBackspace}
                className="h-14 rounded-lg bg-secondary/40 hover:bg-secondary/80 flex items-center justify-center transition-all active:scale-95"
                data-testid="numpad-backspace"
              >
                <Delete className="w-6 h-6" />
              </button>
              <button
                onClick={() => handleNumpad("0")}
                className="h-14 rounded-lg bg-secondary/40 hover:bg-secondary/80 text-xl font-bold font-mono transition-all active:scale-95"
                data-testid="numpad-0"
              >
                0
              </button>
              <button
                onClick={handleConfirm}
                disabled={!inputVal}
                className="h-14 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-bold transition-all active:scale-95 disabled:opacity-40"
                data-testid="numpad-confirm"
              >
                Set
              </button>
            </div>
          </div>

          {/* Bottom actions */}
          <div className="px-5 pb-5 flex gap-2">
            {hasWeight && (
              <Button variant="outline" className="flex-1 text-destructive border-destructive/30 hover:bg-destructive/10" onClick={handleClear} data-testid="weight-clear">
                <X className="w-4 h-4 mr-1" />Clear Weight
              </Button>
            )}
            <Button variant="outline" className="flex-1" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default Header;
